import React, { useState } from 'react';
import { Box, Button, Chip, FormGroup, FormHelperText, SelectChangeEvent, Stack, Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getUploadData, resetFileUpload } from '../../../store/upload/uploadSlice';
import MetadataContainer from '../MetadataContainer';
import ProcessButton from '../../ProcessButton';
import { FieldState, Processors, SheetProcessingRecord } from '../../../types/generic';
import SheetSelector from '../../SheetSelector';
import EncodingSelector from '../../EncodingSelector';
import { checkRequired, handleFieldValueUpdate } from '../../../utils/form';
import FormCheckbox from '../../Form/FormCheckbox';
import Logger from '../../../utils/logger';
import { callAPISync, createUploadHeader, logProcessingStarted, publishMessage, sendSubscribeMessage } from '../../../utils/api';
import { getUserDetails, getUserOrgsString } from '../../../store/user/userSlice';
import { cellebriteSheetInitialState } from '../../../utils/initialStates';
import { genericSheetOptions, getInitialSheets, specialSheetOptions } from '../../../utils/sheets';
import { beginProcessing } from '../../../store/processing/processingSlice';
import { SubscriptionOptions } from '../../../utils/processorTopics';
import { CellebriteSheetMessage } from '../../../types/upload';
import { useLogto } from '@logto/react';
import { UploadFormStep } from '../../Tabs/Upload/UploadTabSkeleton/stepWrapper';
import { useUploadFormStepper } from '../../Tabs/Upload/UploadFormContext';

const CellebriteSheetMetadata: React.FC<{ index?: number }> = ({ index: baseIndex = 0 }) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userDetails = useAppSelector((state) => getUserDetails(state));
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const userTeams = useAppSelector((state) => getUserOrgsString(state));
	const notifyOnComplete = useAppSelector((state) => state.processing.notifyOnComplete);

	const {
		fileName,
		fileOperation,
		deviceOwner,
		deviceOwnerType,
		suspectKnown,
		uploadComplete,
		fileId,
		uploadReason,
		duplicatePreference,
		useCase,
		enrichments,
	} = useAppSelector((state) => getUploadData(state));

	const [fieldValues, setFieldValues] = useState<FieldState>(cellebriteSheetInitialState);

	const [selectorOpen, setSelectorOpen] = useState<boolean>(false);
	const [sheets, setSheets] = useState<SheetProcessingRecord>(getInitialSheets());
	const [specialSheetError, setSpecialSheetError] = useState<string>('');
	const [genericSheetError, setGenericSheetError] = useState<string>('');
	const [publishError, setPublishError] = useState<string>('');

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		handleFieldValueUpdate(e.target.name, checked, fieldValues, setFieldValues);
	};

	const handleSelectChange = (e: SelectChangeEvent<string | number | boolean>) => {
		const { name, value } = e.target;
		handleFieldValueUpdate(name, value, fieldValues, setFieldValues);
	};

	const untagSheet = (sheetName: string) => {
		setSheets({
			...sheets,
			[sheetName]: false,
		});
	};

	const getSheetNames = (): string[] => {
		return Object.entries(sheets)
			.map(([sheet, checked]) => {
				if (checked) {
					return sheet;
				}
			})
			.filter((sheet) => sheet) as string[];
	};

	const onSubmit = () => {
		const tempVals = { ...fieldValues };
		let specialError = '';
		let genericError = '';

		checkRequired(tempVals);

		if (!Object.values(sheets).some((val) => val === true)) {
			specialError = 'At least one sheet must be selected for Special or Generic Processing';
			genericError = specialError;
		}

		if (Object.values(fieldValues).filter((val) => val.error === true).length === 0 && !specialError && !genericError) {
			const cellebriteSheetMessage: CellebriteSheetMessage = {
				...createUploadHeader(deviceOwner, fileName, userDetails, fileOperation, uploadReason, forceDetails, useCase, deviceOwnerType),
				metadata: {
					fileEncoding: fieldValues.encoding.value.toString(),
					sheets: getSheetNames(),
					hyperlinkProcessing: Boolean(fieldValues.hyperlink.value),
					onlyOnDetected: Boolean(fieldValues.detected.value),
					ocrProcessing: Boolean(fieldValues.ocr.value),
				},
			};

			callAPISync(logto, (token) => {
				logProcessingStarted(
					token,
					fileOperation,
					fileName,
					uploadReason,
					fileId,
					userDetails.name,
					userTeams,
					userDetails.email,
					suspectKnown,
					undefined, // undefined as no word list
					undefined,
					notifyOnComplete
				);
				sendSubscribeMessage(SubscriptionOptions.CELLEBRITE_SHEET, token);
				publishMessage(cellebriteSheetMessage, 'upload.cellebrite.sheet', fileId, token, duplicatePreference)
					.then(async (res) => {
						const txt = await res.text();
						if (res.ok) {
							Logger.debug('Submitted:\n', txt);
							dispatch(
								beginProcessing({
									fileName,
									fileId,
									operation: fileOperation,
									processor: Processors.CELLEBRITE_SHEET,
									duplicatePreference,
									enrichments,
								})
							);
							dispatch(resetFileUpload());
						} else {
							Logger.error('Error submitting');
							Logger.error(res.status, res.statusText);
							setPublishError(res.statusText);
						}
					})
					.catch((e) => {
						Logger.error(e);
						setPublishError(JSON.stringify(e));
					});
			});
		} else {
			Logger.debug('Errors found');
		}

		setFieldValues({
			...tempVals,
		});

		setSpecialSheetError(specialError);
		setGenericSheetError(genericError);
	};

	const steps = [
		<UploadFormStep key="sheetMetadata" label="Sheet Metadata" valid={true} validLabel="Sheet Metadata">
			<MetadataContainer title="Sheet Data">
				<EncodingSelector
					name="encoding"
					disabled={!uploadComplete}
					fieldData={fieldValues.encoding}
					setSelectedEncoding={handleSelectChange}
				/>

				<Tooltip title={uploadComplete ? 'Opens a dialog window allowing you to select how to process spreadsheets in the file' : ''}>
					<Button
						variant="contained"
						sx={{ width: '80%', alignSelf: 'center' }}
						disabled={!uploadComplete}
						onClick={() => setSelectorOpen(true)}
						data-testid="sheet-processor-button"
					>
						Select sheets to process
					</Button>
				</Tooltip>

				<FormGroup>
					<Tooltip title="Sheets which are currently selected for specialised processing" placement="bottom-start">
						<Typography component="h3" variant="h6" color={uploadComplete ? 'text.primary' : 'text.disabled'}>
							Special Processing
						</Typography>
					</Tooltip>
					<Stack direction={'row'} spacing={1}>
						<FormGroup row>
							{Object.entries(sheets).map(
								([key, val]) =>
									val &&
									specialSheetOptions.some((opt) => opt === key) && (
										<Chip
											data-testid={`special-proc-chip-${key}`}
											key={`special-proc-chip-${key}`}
											label={key}
											onDelete={() => untagSheet(key)}
											sx={{ margin: 1 }}
										/>
									)
							)}
						</FormGroup>
					</Stack>
					{specialSheetError && (
						<FormHelperText error data-testid="special-sheet-helper-text">
							{specialSheetError}
						</FormHelperText>
					)}
				</FormGroup>

				<FormGroup>
					<Tooltip title="Sheets which are currently selected to be processed with best efforts" placement="bottom-start">
						<Typography component="h3" variant="h6" color={uploadComplete ? 'text.primary' : 'text.disabled'}>
							Generic Processing
						</Typography>
					</Tooltip>
					<Stack direction={'row'} spacing={1}>
						<FormGroup row>
							{Object.entries(sheets).map(
								([key, val]) =>
									val &&
									genericSheetOptions.some((opt) => opt === key) && (
										<Chip
											data-testid={`generic-proc-chip-${key}`}
											key={`generic-proc-chip-${key}`}
											label={key}
											onDelete={() => untagSheet(key)}
											sx={{ margin: 1 }}
										/>
									)
							)}
						</FormGroup>
					</Stack>
					{genericSheetError && (
						<FormHelperText error data-testid="generic-sheet-helper-text">
							{genericSheetError}
						</FormHelperText>
					)}
				</FormGroup>

				<FormGroup>
					<FormCheckbox
						fieldData={fieldValues.hyperlink}
						label={'Enable Hyperlink Processing'}
						checkboxProps={{
							name: 'hyperlink',
							disabled: !uploadComplete,
							onChange: handleCheckboxChange,
						}}
					/>

					<Box sx={{ paddingLeft: 2 }}>
						<FormCheckbox
							fieldData={fieldValues.detected}
							label={'Only on Detected Content?'}
							checkboxProps={{
								name: 'detected',
								disabled: !fieldValues.hyperlink.value || !uploadComplete,
								onChange: handleCheckboxChange,
								checked: Boolean(fieldValues.hyperlink.value) && Boolean(fieldValues.detected.value),
							}}
						/>
					</Box>

					<FormCheckbox
						fieldData={fieldValues.ocr}
						label={'OCR Processing'}
						checkboxProps={{
							name: 'ocr',
							disabled: !uploadComplete,
							onChange: handleCheckboxChange,
						}}
					/>
				</FormGroup>

				<SheetSelector
					key={selectorOpen ? 'sheet-selector-open' : 'sheet-selector-closed'}
					isOpen={selectorOpen}
					setOpen={setSelectorOpen}
					sheets={sheets}
					updateSheets={(newSheets) => {
						setSpecialSheetError('');
						setGenericSheetError('');
						setSelectorOpen(false);
						setSheets(newSheets);
					}}
				/>
			</MetadataContainer>
		</UploadFormStep>,
		<UploadFormStep key="cellebriteSheetProcess" label="Final Confirmation" valid={true} validLabel="">
			<ProcessButton
				operation={fileOperation}
				onClick={onSubmit}
				pipelineName="Cellebrite"
				publishError={publishError}
				setPublishError={setPublishError}
			/>
		</UploadFormStep>,
	];

	const { activeStep } = useUploadFormStepper();

	return (
		<>
			{steps.map((step, i) => {
				const stepIndex = baseIndex + i;

				return React.cloneElement(step, {
					index: stepIndex,
					active: activeStep === stepIndex,
					completed: activeStep > stepIndex,
				});
			})}
		</>
	);
};

export default CellebriteSheetMetadata;
